import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import Canvas from '../components/Canvas'; // Import the Canvas component
import './PublicInvitationPage.css';

// Create a slug utility function
const createSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

function PublicInvitationPage() {
  // Use hooks at the top level
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [event, setEvent] = useState(null);
  
  // Add device detection state
  const [deviceType, setDeviceType] = useState('desktop');
  
  // Add a state to track if we've fixed element positions for the current device
  const [positionsAdjusted, setPositionsAdjusted] = useState(false);
  
  // Detect device type based on screen width - simplified to just mobile or desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setDeviceType('mobile');
      } else {
        setDeviceType('desktop');
      }
    };
    
    // Initial detection
    handleResize();
    
    // Update on resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Create stable no-op functions - we only want to display, not edit
  const noOp = useMemo(() => () => {}, []);
  const getDefaultStyle = useMemo(() => () => ({}), []);
  
  // Load data effect
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('🔵 Fetching public invitation data for:', params);
        
        // Step 1: Get all events 
        const eventsRef = collection(db, 'events');
        const eventsSnapshot = await getDocs(eventsRef);
        
        // Find event with matching slug and userId
        let matchedEvent = null;
        eventsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const thisEventSlug = createSlug(data.name || '');
          const eventUserId = data.userId?.substring(0, 5);
          
          if (thisEventSlug === params.eventSlug && eventUserId === params.userId) {
            matchedEvent = { id: doc.id, ...data };
          }
        });
        
        if (!matchedEvent) {
          setError("Event not found");
          setLoading(false);
          return;
        }
        
        setEvent(matchedEvent);
        
        // Step 2: Get invitations for this event
        const invitationsRef = collection(db, 'invitations');
        const q = query(
          invitationsRef,
          where('eventId', '==', String(matchedEvent.id)),
          where('published', '==', true)
        );
        
        const querySnapshot = await getDocs(q);
        
        // Find invitation with matching slug
        let foundInvitation = null;
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          // Extract the base invitation slug without the unique ID suffix
          const urlInvitationSlug = params.invitationSlug;
          
          // The URL parameter might contain the unique ID suffix (-xxxxx)
          // Handle both formats: with and without the unique ID suffix
          let invitationNameSlug = createSlug(data.name || '');
          
          console.log('🔵 Comparing invitation slugs:', {
            urlSlug: urlInvitationSlug,
            invitationNameSlug: invitationNameSlug,
            invitationId: doc.id
          });
          
          // Check if URL matches either the base slug or starts with the slug followed by a hyphen
          if (urlInvitationSlug === invitationNameSlug || 
              urlInvitationSlug.startsWith(invitationNameSlug + '-')) {
            
            // If we have a unique ID suffix, also confirm it matches the invitation ID
            if (urlInvitationSlug.includes('-')) {
              const uniqueIdSuffix = urlInvitationSlug.split('-').pop();
              const invitationIdPrefix = doc.id.substring(0, uniqueIdSuffix.length);
              
              if (uniqueIdSuffix === invitationIdPrefix) {
                console.log('🔵 Found invitation by name and unique ID match');
                foundInvitation = { id: doc.id, ...data };
              }
            } else {
              // For backward compatibility with old URLs without unique ID
              console.log('🔵 Found invitation by name match (legacy URL format)');
              foundInvitation = { id: doc.id, ...data };
            }
          }
        });
        
        if (!foundInvitation) {
          setError("Invitation not found");
          setLoading(false);
          return;
        }
        
        setInvitation(foundInvitation);
        setLoading(false);
        
      } catch (err) {
        console.error("Error loading invitation:", err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [params]);
  
  // Update elements for responsive behavior without reordering
  useEffect(() => {
    if (!invitation || !invitation.elements || positionsAdjusted) return;
    
    // Clone the elements to avoid mutating the original data
    const adjustedElements = JSON.parse(JSON.stringify(invitation.elements));
    
    // Add a flag to indicate we've processed the elements for responsive behavior
    adjustedElements.forEach(element => {
      // Store original positions for each element
      if (!element.originalPosition) {
        element.originalPosition = { ...element.position };
      }
      
      // Add metadata to help with responsive behavior
      element.preservePosition = true;
    });
    
    // Update the invitation with adjusted elements
    setInvitation({
      ...invitation,
      elements: adjustedElements
    });
    
    setPositionsAdjusted(true);
  }, [invitation, deviceType, positionsAdjusted]);
  
  // Render states
  if (loading) {
    return <div className="loading">Loading invitation...</div>;
  }
  
  if (error) {
    return (
      <div className="error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }
  
  // Render invitation with Canvas in preview mode - identical to editor preview
  return (
    <div className="public-invitation-container">
      <Canvas
        elements={invitation.elements || []}
        selectedElement={null}
        setSelectedElement={noOp}
        updateElement={noOp}
        deleteElement={noOp}
        isPreviewMode={true}
        addElement={noOp}
        getDefaultStyle={getDefaultStyle}
        deviceView={deviceType}
        eventId={event?.id}
        invitationId={invitation?.id}
      />
    </div>
  );
}

export default PublicInvitationPage; 